import { useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { api, idr } from "../../lib/api";
import { formatTanggal } from "../../lib/tanggal";

/** Pengaju batal validasi = orang yang sedang login? Cermin
 *  `piutang.py::_orang_yang_sama` (email ATAU id) — tombol Setujui/Tolak tidak
 *  ditampilkan untuk pengajunya sendiri karena server menolaknya 403. */
export function sayaPengajuBatal(pb, user) {
  const a = (pb?.email || "").trim().toLowerCase();
  const b = (user?.email || "").trim().toLowerCase();
  return (!!a && a === b) || (!!pb?.user_id && pb.user_id === user?.id);
}

/** Satu modal untuk tiga langkah alur batal validasi (B1, brief 22 Sep):
 *  "ajukan" (alasan WAJIB), "setujui" dan "tolak" (catatan opsional). Isi
 *  pengajuan ditampilkan apa adanya supaya SPV memutuskan berdasarkan alasan
 *  tertulis, bukan cerita lisan.
 *
 *  Dipakai layar Piutang DAN Pusat Approval (keputusan 23 Sep B1: "SPV
 *  menyetujui/menolak — layar Piutang & Pusat Approval"). SATU komponen,
 *  endpoint yang sama, supaya klaim atomik & larangan "pengaju menyetujui
 *  sendiri" tetap hidup di satu tempat di server dan dua layar tidak berpisah
 *  jalan (CLAUDE.md: duplikasi melahirkan penyimpangan).
 *
 *  `piutang` minimal: {id, code, pelanggan_nama, nominal, validasi_oleh,
 *  validasi_pada, validasi_pengajuan_batal: {oleh, alasan}}. */
export default function ModalBatalValidasi({ mode, piutang, onBatal, onSelesai }) {
  const [teks, setTeks] = useState("");
  const [busy, setBusy] = useState(false);
  const pb = piutang.validasi_pengajuan_batal || {};
  const judul = { ajukan: "Ajukan Batal Validasi", setujui: "Setujui Batal Validasi",
                  tolak: "Tolak Pengajuan Batal" }[mode];
  const wajib = mode === "ajukan";
  const kirim = async () => {
    setBusy(true);
    try {
      const url = `/finance/piutang/${piutang.id}/validasi/${
        mode === "ajukan" ? "ajukan-batal" : mode === "setujui" ? "setujui-batal" : "tolak-batal"}`;
      const body = mode === "ajukan" ? { alasan: teks.trim() } : { catatan: teks.trim() || null };
      const { data } = await api.post(url, body);
      toast.success(data.message || "Tersimpan");
      onSelesai();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onBatal}>
      <div onClick={e => e.stopPropagation()} data-testid="piu-batal-validasi-modal"
           className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-lg">{judul}</h3>
          <button onClick={onBatal} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="bg-slate-50 rounded-lg p-3 text-sm mb-3 space-y-1">
          <p className="font-medium text-slate-900">{piutang.pelanggan_nama}</p>
          <p className="text-xs text-slate-500 font-mono">{piutang.code} · {idr(piutang.nominal)}</p>
          <p className="text-xs text-slate-600">
            Divalidasi {piutang.validasi_oleh || "-"}
            {piutang.validasi_pada ? ` · ${formatTanggal(piutang.validasi_pada, { denganJam: true })}` : ""}
          </p>
          {mode !== "ajukan" && (
            <p className="text-xs text-violet-800 bg-violet-50 border border-violet-100 rounded px-2 py-1"
               data-testid="piu-batal-validasi-alasan">
              Diajukan <b>{pb.oleh || "-"}</b>: {pb.alasan || "-"}
            </p>
          )}
        </div>
        {mode === "ajukan" && (
          <p className="text-[11px] text-slate-500 mb-2">
            Centang Valid tidak bisa dilepas langsung. Pengajuan ini diputuskan SPV lain
            (bukan Anda sendiri); setelah disetujui, piutang dan notanya bisa dikoreksi lagi.
          </p>
        )}
        {mode === "setujui" && (
          <p className="text-[11px] text-amber-700 mb-2">
            Setelah disetujui, centang Valid lepas dan koreksi pembayaran / edit nota bertempo
            terbuka lagi. Jejak keputusan ini tersimpan di Riwayat Validasi.
          </p>
        )}
        <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
          {wajib ? "Alasan pembatalan (wajib)" : "Catatan (opsional)"}
        </label>
        <textarea value={teks} onChange={e => setTeks(e.target.value)} rows={3}
                  data-testid="piu-batal-validasi-teks"
                  placeholder={wajib ? "Contoh: nominal transfer ternyata kurang, salah centang" : ""}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:border-violet-500" />
        <div className="flex gap-2 mt-4">
          <button onClick={onBatal} className="flex-1 h-10 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm">Batal</button>
          <button disabled={busy || (wajib && !teks.trim())} data-testid="piu-batal-validasi-kirim"
                  onClick={kirim}
                  className={`flex-1 h-10 rounded-lg text-white text-sm font-medium disabled:opacity-50 ${
                    mode === "tolak" ? "bg-rose-600 hover:bg-rose-700"
                    : mode === "setujui" ? "bg-emerald-600 hover:bg-emerald-700"
                    : "bg-violet-600 hover:bg-violet-700"}`}>
            {busy ? "..." : judul}
          </button>
        </div>
      </div>
    </div>
  );
}
